import {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  ReactNode,
} from 'react';
import { ProductMeta, TOCNode } from '../types/DocumentTypes';
import { fetchDocumentTOC } from '../utils/DocumentUtils';
import { useUserManagement } from '../features/UserManagement/components/UserManagementContext';
import { useParams } from 'react-router';
import { ReaderRouteParams } from '../types/RouteParams';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { productMetaQuery } from '../utils/PrefetchUtils';

// ============================================================================
// Types
// ============================================================================
type DocumentContextState = 'loading' | 'ready' | 'error' | 'access-denied';

interface LicenseValidationResult {
  hasAccess: boolean;
  loading: boolean;
  error?: string;
}

interface DocumentContextValue {
  state: DocumentContextState;
  productMeta: ProductMeta | null;
  chapters: TOCNode[] | null;
  id: string;
  licenseValidation: LicenseValidationResult;
}

// ============================================================================
// Context Creation
// ============================================================================
// Create context with default values
const DocumentContext = createContext<DocumentContextValue>({
  state: 'loading',
  productMeta: null,
  chapters: null,
  id: '',
  licenseValidation: {
    hasAccess: false,
    loading: true,
  },
});

// ============================================================================
// Provider Component
// ============================================================================

export function DocumentContextProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const { isbn: id } = useParams<ReaderRouteParams>();
  const { data: prefetchedProductMeta, isPending } = useQuery(
    productMetaQuery(id as string),
  );
  const [state, setState] = useState<DocumentContextState>('loading');
  const [productMeta, setProductMeta] = useState<ProductMeta | null>(
    !isPending ? (prefetchedProductMeta ?? null) : null,
  );
  const [chapters, setChapters] = useState<TOCNode[] | null>(null);
  const [licenseError, setLicenseError] = useState<string | undefined>();
  const { userProducts, identity } = useUserManagement();
  const queryClient = useQueryClient();

  // Single effect to handle the entire document loading and validation flow
  useEffect(() => {
    // Check that the document ID is available in the path
    if (!id) {
      setState('error');
      return;
    }

    if (identity?.loading) {
      return;
    }

    const loadAndValidate = async () => {
      setState('loading');
      setLicenseError(undefined);

      try {
        const fetchedProductMeta = await queryClient.ensureQueryData(
          productMetaQuery(id),
        );

        if (fetchedProductMeta) {
          setProductMeta(fetchedProductMeta);
          document.title = fetchedProductMeta.title;
        } else {
          throw new Error('Failed to load product metadata');
        }

        // Validate license access
        const requiredProducts = fetchedProductMeta.relatedProducts || [];
        const hasAccess = validateLicenseAccess(userProducts, requiredProducts);

        if (!hasAccess) {
          setState('access-denied');
          return;
        }

        const fetchedChapters = await fetchDocumentTOC(
          id,
          await identity?.actions?.getAccessToken(),
        );

        setChapters(fetchedChapters);

        setState('ready');
      } catch (err) {
        setLicenseError(
          err instanceof Error ? err.message : 'Failed to load document',
        );
        setState('error');
      }
    };
    loadAndValidate();
  }, [id, userProducts, identity, queryClient]);

  // License validation for UI components (computed from current state)
  const licenseValidation = useMemo<LicenseValidationResult>(() => {
    if (state === 'loading') {
      return {
        hasAccess: false,
        loading: true,
      };
    }

    if (licenseError) {
      return {
        hasAccess: false,
        loading: false,
        error: licenseError,
      };
    }

    return {
      hasAccess: state === 'ready',
      loading: false,
    };
  }, [state, licenseError]);

  const contextValue = useMemo<DocumentContextValue>(
    () => ({
      state,
      productMeta,
      chapters,
      licenseValidation,
      id: id ?? '',
    }),
    [state, productMeta, id, licenseValidation, chapters],
  );

  return (
    <DocumentContext.Provider value={contextValue}>
      {children}
    </DocumentContext.Provider>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Core license validation logic
 */
function validateLicenseAccess(
  userProducts: string[],
  requiredProducts: string[],
): boolean {
  // If no products or rights, revoke access
  if (requiredProducts.length === 0 || userProducts.length === 0) {
    return false;
  }

  // User has access if ANY of their products matches ANY of the book's requirements
  return userProducts.some((userProduct) =>
    requiredProducts.includes(userProduct),
  );
}

// ============================================================================
// Custom Hook
// ============================================================================

/**
 * Custom hook to use the document context
 * @throws Error if used outside of DocumentProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useDocumentContext() {
  const context = useContext(DocumentContext);

  if (!context) {
    throw new Error(
      'useDocumentContext must be used within a DocumentProvider',
    );
  }

  return context;
}
